import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ShieldCheck,
  ShieldAlert,
  History as HistoryIcon,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import { Badge, Button, FormField, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { cn } from "@/design-system/lib/utils";
import {
  certificationsApi,
  type CertificationRecordDto,
  type EntityCertificationsDto,
  type EntityType,
  type GateDto,
  type GateItemDto,
} from "@/api/certifications";
import { RoleChip } from "@/components/shared/RoleChip";
import { formatDateInTz, useDateFormatter } from "@/utils/datetime";

/**
 * Certifications Module v1.0 §4b — reusable Certifications tab. Used by
 * every entity detail page that integrates with the module (Broker first
 * in PR 3; Loan, Deal, Vendor, SPV, Investor in future modules).
 *
 * Layout: Checklist (default) shows ONLY the entity's current gate — the
 * one item set the user must satisfy before the next status change. Past
 * gates the entity has already crossed are not shown here; they live on
 * the History sub-tab as the audit trail. PR 4 simplification —
 * previously the Checklist also rendered "upcoming" gates beneath the
 * current one, which produced misleading "UPCOMING" badges on past gates
 * for already-advanced entities.
 *
 * Empty state: when there is no current gate (e.g., a Certified broker
 * or any terminal stage), Checklist shows a polite "all gates satisfied"
 * message with a one-click jump to History.
 *
 * Role authorization: per-definition <c>RequiredRoles</c> /
 * <c>RevokeRoles</c> control whether the Certify / Revoke buttons are
 * enabled. ADMIN is not a wildcard (Standard P4) — admins can act only
 * on definitions where ADMIN is explicitly listed.
 */
export interface CertificationsTabProps {
  entityType: EntityType;
  entityId: string;
  /** From the entity's status field. Drives gate grouping. */
  currentStage: string;
  /**
   * Called after every successful certify or revoke so the parent page
   * can refresh status badges, gate banners, etc.
   */
  onCertificationChange?: () => void;
}

type SubView = "checklist" | "history";

export function CertificationsTab({
  entityType,
  entityId,
  currentStage,
  onCertificationChange,
}: CertificationsTabProps) {
  const qc = useQueryClient();
  const queryKey = useMemo(
    () => ["certifications", entityType, entityId] as const,
    [entityType, entityId],
  );

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => certificationsApi.listForEntity(entityType, entityId),
  });

  const [view, setView] = useState<SubView>("checklist");
  const [certifyTarget, setCertifyTarget] = useState<{
    item: GateItemDto;
    gate: GateDto;
  } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<{
    item: GateItemDto;
    record: CertificationRecordDto;
  } | null>(null);
  const [historyDefinitionId, setHistoryDefinitionId] = useState<string>("");

  const refresh = () => {
    void qc.invalidateQueries({ queryKey });
    onCertificationChange?.();
  };

  if (isLoading) {
    return (
      <div className="text-caption text-gray-mid italic py-space-4">
        Loading certifications…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-caption text-status-error py-space-4">
        {error instanceof ApiError ? error.message : "Failed to load certifications."}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-space-4">
      {/* Sub-view toggle */}
      <nav className="inline-flex border border-border-light rounded-md overflow-hidden text-small">
        <SubTab active={view === "checklist"} onClick={() => setView("checklist")}>
          <ShieldCheck size={14} aria-hidden /> Checklist
        </SubTab>
        <SubTab active={view === "history"} onClick={() => setView("history")}>
          <HistoryIcon size={14} aria-hidden /> History
        </SubTab>
      </nav>

      {view === "checklist" &&
        (data.currentGate === null ? (
          <EmptyState
            currentStage={currentStage}
            historyCount={data.history.length}
            onViewHistory={() => setView("history")}
          />
        ) : (
          <GateSection
            gate={data.currentGate}
            onCertify={(item) =>
              setCertifyTarget({ item, gate: data.currentGate! })
            }
            onRevoke={(item, record) => setRevokeTarget({ item, record })}
          />
        ))}

      {view === "history" && (
        <HistoryView
          history={data.history}
          definitionFilter={historyDefinitionId}
          onFilterChange={setHistoryDefinitionId}
        />
      )}

      {certifyTarget && (
        <CertifyModal
          entityType={entityType}
          entityId={entityId}
          item={certifyTarget.item}
          gate={certifyTarget.gate}
          onClose={() => setCertifyTarget(null)}
          onSaved={() => {
            setCertifyTarget(null);
            refresh();
          }}
        />
      )}
      {revokeTarget && (
        <RevokeModal
          entityType={entityType}
          entityId={entityId}
          item={revokeTarget.item}
          record={revokeTarget.record}
          onClose={() => setRevokeTarget(null)}
          onSaved={() => {
            setRevokeTarget(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/* -------------------- Sub-tab toggle -------------------- */

function SubTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-space-3 py-space-2 inline-flex items-center gap-space-2 font-display uppercase tracking-wide",
        active ? "bg-gold/10 text-onyx" : "text-gray-mid hover:text-onyx",
      )}
    >
      {children}
    </button>
  );
}

/* -------------------- Empty state -------------------- */

function EmptyState({
  currentStage,
  historyCount,
  onViewHistory,
}: {
  currentStage: string;
  historyCount: number;
  onViewHistory: () => void;
}) {
  return (
    <div className="bg-white-pure border border-border-light rounded-lg p-space-5 text-center">
      <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gold/10 mb-space-2">
        <ShieldCheck size={18} className="text-gold-shadow" aria-hidden />
      </div>
      <p className="text-small text-gray-mid">
        No certifications pending. All gates for the current stage
        <span className="font-mono"> ({currentStage}) </span>
        have been satisfied.
      </p>
      {historyCount > 0 && (
        <button
          type="button"
          onClick={onViewHistory}
          className="mt-space-2 text-caption text-gold-shadow hover:underline"
        >
          View History ({historyCount} record{historyCount === 1 ? "" : "s"}) →
        </button>
      )}
    </div>
  );
}

/* -------------------- Gate section (Checklist) -------------------- */
// PR 4 — only the entity's current gate is rendered, so the "Upcoming"
// badge that used to appear on subsequent gates is gone. The header now
// just labels the gate by its (stageFrom → stageTo) transition.

function GateSection({
  gate,
  onCertify,
  onRevoke,
}: {
  gate: GateDto;
  onCertify: (item: GateItemDto) => void;
  onRevoke: (item: GateItemDto, record: CertificationRecordDto) => void;
}) {
  const heading = `${gate.stageFrom ?? "(any)"} → ${gate.stageTo}`;
  return (
    <section className="bg-white-pure border border-border-light rounded-lg">
      <header className="px-space-3 py-space-2 border-b border-border-light flex items-center gap-space-2">
        <span className="font-display font-semibold text-caption uppercase tracking-wider text-gold-shadow">
          {heading}
        </span>
        <Badge status="gold">Current gate</Badge>
      </header>
      <ul className="divide-y divide-border-light">
        {gate.items
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((item) => (
            <ChecklistRow
              key={item.definitionId}
              item={item}
              onCertify={() => onCertify(item)}
              onRevoke={(rec) => onRevoke(item, rec)}
            />
          ))}
      </ul>
    </section>
  );
}

function ChecklistRow({
  item,
  onCertify,
  onRevoke,
}: {
  item: GateItemDto;
  onCertify: () => void;
  onRevoke: (record: CertificationRecordDto) => void;
}) {
  const { hasRoleCode } = useAuth();
  const { tz } = useDateFormatter();
  const canCertify = item.requiredRoles.some(hasRoleCode);
  const canRevoke = item.revokeRoles.some(hasRoleCode);
  const record = item.activeRecord;
  const [noteOpen, setNoteOpen] = useState(false);

  let state: "not-certified" | "certified" | "revoked";
  if (!record) state = "not-certified";
  else if (record.revokedAt) state = "revoked";
  else state = "certified";

  return (
    <li className="px-space-3 py-space-3">
      <div className="flex items-start justify-between gap-space-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-space-2">
            {state === "certified" && (
              <CheckCircle2
                size={14}
                className="text-status-success shrink-0"
                aria-hidden
              />
            )}
            {state === "not-certified" && (
              <ShieldAlert size={14} className="text-gray-mid shrink-0" aria-hidden />
            )}
            {state === "revoked" && (
              <XCircle size={14} className="text-status-error shrink-0" aria-hidden />
            )}
            <h4
              className={cn(
                "font-semibold text-small",
                state === "revoked" && "line-through text-gray-mid",
              )}
            >
              {item.label}
            </h4>
          </div>
          {item.description && (
            <p className="text-caption text-gray-mid mt-space-1">{item.description}</p>
          )}
          {state === "certified" && record && (
            <div className="mt-space-2 text-caption text-gray-mid">
              <span className="text-onyx">
                {record.certifiedByDisplayName ?? record.certifiedByUserId}
              </span>{" "}
              certified {timeAgo(record.certifiedAt, tz)}.
              {record.note && (
                <button
                  type="button"
                  className="ml-space-2 text-gold-shadow hover:underline"
                  onClick={() => setNoteOpen((v) => !v)}
                >
                  {noteOpen ? "Hide note" : "Show note"}
                </button>
              )}
              {noteOpen && record.note && (
                <p className="mt-space-1 italic whitespace-pre-wrap text-onyx">
                  &ldquo;{record.note}&rdquo;
                </p>
              )}
            </div>
          )}
          {state === "revoked" && record && (
            <div className="mt-space-2 text-caption text-status-error">
              Revoked {timeAgo(record.revokedAt!, tz)}
              {record.revokedReason ? ` — ${record.revokedReason}` : ""}.
            </div>
          )}
          <div className="mt-space-2 flex flex-wrap gap-space-1">
            {item.requiredRoles.map((r) => (
              <RoleChip key={r} code={r} status="gold" />
            ))}
          </div>
        </div>

        <div className="shrink-0">
          {state !== "certified" && (
            <Button
              onClick={onCertify}
              disabled={!canCertify}
              title={
                canCertify
                  ? undefined
                  : `You need one of: ${item.requiredRoles.join(", ")}`
              }
            >
              Certify
            </Button>
          )}
          {state === "certified" && record && (
            <Button
              variant="secondary"
              onClick={() => onRevoke(record)}
              disabled={!canRevoke}
              title={
                canRevoke
                  ? undefined
                  : `You need one of: ${item.revokeRoles.join(", ")}`
              }
            >
              Revoke
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

/* -------------------- History view -------------------- */

function HistoryView({
  history,
  definitionFilter,
  onFilterChange,
}: {
  history: CertificationRecordDto[];
  definitionFilter: string;
  onFilterChange: (v: string) => void;
}) {
  const { dateTime } = useDateFormatter();
  const definitions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of history) seen.set(r.definitionId, r.definitionLabel);
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [history]);

  const rows = definitionFilter
    ? history.filter((r) => r.definitionId === definitionFilter)
    : history;

  if (history.length === 0) {
    return (
      <div className="bg-white-pure border border-border-light rounded-lg p-space-4 text-caption text-gray-mid italic">
        No certification activity yet.
      </div>
    );
  }
  return (
    <div className="space-y-space-3">
      {definitions.length > 1 && (
        <FormField label="Filter by definition">
          <select
            className="border border-border-light rounded-md p-space-2 text-small"
            value={definitionFilter}
            onChange={(e) => onFilterChange(e.target.value)}
          >
            <option value="">All definitions</option>
            {definitions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </FormField>
      )}
      <ul
        aria-label="Certification history"
        className="divide-y divide-border-light bg-white-pure border border-border-light rounded-lg"
      >
        {rows.map((r) => (
          <li key={r.id} className="px-space-3 py-space-2 text-small">
            <div className="flex items-center gap-space-2">
              {r.revokedAt ? (
                <Badge status="error">Revoked</Badge>
              ) : (
                <Badge status="finalized">Active</Badge>
              )}
              <span className="font-semibold">{r.definitionLabel}</span>
            </div>
            <div className="text-caption text-gray-mid mt-space-1">
              {r.certifiedByDisplayName ?? r.certifiedByUserId} ·{" "}
              {dateTime(r.certifiedAt)}
              {r.certifiedAsRoles.length > 0 && (
                <> · as {r.certifiedAsRoles.join(", ")}</>
              )}
            </div>
            {r.note && (
              <p className="mt-space-1 italic text-caption whitespace-pre-wrap">
                &ldquo;{r.note}&rdquo;
              </p>
            )}
            {r.revokedAt && (
              <div className="mt-space-1 text-caption text-status-error">
                Revoked {dateTime(r.revokedAt)} by{" "}
                {r.revokedByDisplayName ?? r.revokedByUserId}
                {r.revokedReason ? ` — ${r.revokedReason}` : ""}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------- Certify modal -------------------- */

function CertifyModal({
  entityType,
  entityId,
  item,
  gate,
  onClose,
  onSaved,
}: {
  entityType: EntityType;
  entityId: string;
  item: GateItemDto;
  gate: GateDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const heldRoles = useMemo(() => {
    const set = new Set(user?.roleCodes ?? []);
    return item.requiredRoles.filter((r) => set.has(r));
  }, [user, item.requiredRoles]);
  const identity = user
    ? `You are certifying as ${user.displayName ?? user.email ?? user.id}` +
      (heldRoles.length > 0 ? ` — role ${heldRoles.join(", ")}` : "")
    : "You are certifying.";

  const mutation = useMutation({
    mutationFn: () =>
      certificationsApi.certify(entityType, entityId, {
        definitionId: item.definitionId,
        note: note.trim() === "" ? null : note.trim(),
      }),
    onSuccess: () => {
      toast.success("Certification recorded.");
      onSaved();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Certify: ${item.label}`}
      description={`Gate ${gate.stageFrom ?? "(any)"} → ${gate.stageTo}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            loading={mutation.isPending}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            Certify
          </Button>
        </>
      }
    >
      {item.description && (
        <p className="text-small text-gray-mid mb-space-2">{item.description}</p>
      )}
      <p className="text-caption text-gray-mid mb-space-3">{identity}</p>
      <FormField label="Note (optional)">
        <Textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional context for the audit trail."
        />
      </FormField>
      {error && (
        <p className="text-caption text-status-error mt-space-2" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

/* -------------------- Revoke modal -------------------- */

function RevokeModal({
  entityType,
  entityId,
  item,
  record,
  onClose,
  onSaved,
}: {
  entityType: EntityType;
  entityId: string;
  item: GateItemDto;
  record: CertificationRecordDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const { dateTime } = useDateFormatter();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      certificationsApi.revoke(entityType, entityId, record.id, {
        reason: reason.trim(),
      }),
    onSuccess: () => {
      toast.info("Certification revoked.");
      onSaved();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Revoke: ${item.label}`}
      description={`Active record from ${dateTime(record.certifiedAt)}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            disabled={reason.trim() === ""}
            loading={mutation.isPending}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            Revoke
          </Button>
        </>
      }
    >
      <div className="bg-status-warning/10 border border-status-warning/30 rounded-md p-space-3 text-caption flex gap-space-2">
        <AlertTriangle
          size={14}
          className="text-status-warning shrink-0 mt-[2px]"
          aria-hidden
        />
        <span>
          Revoked certifications cannot be reactivated. To re-establish certification,
          an authorized user must create a new record.
        </span>
      </div>
      <FormField label="Reason" required className="mt-space-3">
        <Textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why does the prior attestation no longer hold?"
        />
      </FormField>
      {error && (
        <p className="text-caption text-status-error mt-space-2" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

/* -------------------- Helpers -------------------- */

function timeAgo(iso: string, tz: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = Math.round(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  if (days < 30) return `${days}d ago`;
  return formatDateInTz(iso, tz);
}

// Suppress unused warning when EntityCertificationsDto is referenced
// only as a generic; importing keeps the type available for callers who
// want to construct test fixtures.
export type { EntityCertificationsDto };
